import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import {
  removeScriptFromString,
  removeTagsFromString,
} from '../../utilities/parsers/htmlParsers'
import { Hr } from '../Hr'
import type { SocialIconName } from '../Links/SocialLink'
import { SocialLink } from '../Links/SocialLink'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Follow.module.scss'

export const Follow: FunctionComponent<FollowProps> = ({
  links,
  centered = false,
  headline = '',
  className,
  location = 'top',
  ...rest
}) => {
  const createFollowLink = ({ icon, href, linkText }: FollowLinkType) => {
    const ariaLabel = removeTagsFromString(linkText)
    const displayBlock = !headline
    const dataWhichId = `social-follow-${icon}-${location}`

    return (
      <li
        className={classnames(
          styles.followListItem,
          displayBlock && styles.followListItemBlock
        )}
        key={icon}
      >
        <SocialLink
          href={href}
          icon={icon}
          aria-label={ariaLabel}
          data-which-id={dataWhichId}
        >
          {displayBlock && linkText}
        </SocialLink>
      </li>
    )
  }

  return (
    <div {...{ className, ...rest }}>
      <Hr className={styles.followHeader} />
      <section
        data-testid="follow-component"
        className={classnames(
          styles.followComponent,
          centered && headline && styles.followCentered
        )}
      >
        {headline && (
          <TypographyV2
            tag="h4"
            textStyle="sb-text-interface-body-small-regular"
            data-testid="follow-component-headline"
            className={classnames(
              styles.followHeadlineText,
              !headline && styles.followHeadlineTextPrintHide,
              centered && headline && styles.followHeadlineTextCenter
            )}
            // This is being done as the string being passed in contains html element tags for styling (eg <b>).
            // This is currently the best way we can find to get the tags to render inside the span.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: removeScriptFromString(headline),
            }}
          />
        )}
        <ul
          className={classnames(
            styles.followLinksWrapper,
            headline
              ? styles.followLinksWrapperHeadline
              : styles.followLinksWrapperHeadlineColumn,
            centered && styles.followLinksWrapperHeadlineCentered
          )}
        >
          {links.map(createFollowLink)}
        </ul>
      </section>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type FollowProps = {
  /**
   * Array of link objects to generate the individual links
   */
  links: Array<FollowLinkType>
  /**
   * Boolean value to determine whether the links are centered vertically without text
   */
  centered?: boolean
  /**
   * Optional headline text to replace the default headline
   */
  headline?: string
  /**
   * Class name that can be added to add extra styling to the component
   */
  className?: string
  /**
   * Location on page, used for data tracking
   */
  location?: 'top' | 'bottom'
} & HTMLProps<HTMLDivElement>

export type FollowLinkType = {
  icon: SocialIconName
  linkText: string
  href: string
}
